import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, StatusBadge, fmtUSD, fmtDate } from "@/components/ui-bits";

const PAGE_SIZE = 10;

export default function AdminUsers() {
  const [rows, setRows] = useState([]);
  const [addAmt, setAddAmt] = useState({});
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const load = async (q = "") => {
    const { data } = await api.get(`/admin/users${q ? `?search=${encodeURIComponent(q)}` : ""}`);
    setRows(data); setPage(1);
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const t = setTimeout(() => load(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (path, uid, body) => {
    try {
      await api.post(`/admin/users/${uid}/${path}`, body || {});
      toast.success("Done"); load(search.trim());
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const pageRows = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div>
      <PageHeader title="Users" subtitle={`${rows.length} accounts`}
        action={
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#475569]" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by ID, email, wallet…"
              data-testid="user-search"
              className="bg-[#0A132A] border border-white/10 rounded-lg pl-9 pr-3 py-2 text-sm w-64 max-w-full focus:outline-none focus:border-[#00F0FF]"
            />
          </div>
        }
      />
      <div className="glass p-5 overflow-x-auto">
        <table className="w-full text-sm min-w-[1000px]">
          <thead className="text-[11px] uppercase tracking-wider text-[#475569]">
            <tr className="border-b border-white/5">
              <th className="text-left py-2">User ID</th>
              <th className="text-left">Email</th>
              <th className="text-right">Funding</th>
              <th className="text-right">Rewards</th>
              <th className="text-right">Plans</th>
              <th className="text-left pl-4">Wallet</th>
              <th className="text-right">Registered</th>
              <th className="text-right">Status</th>
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="admin-users-table">
            {pageRows.map((u) => (
              <tr key={u.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                <td className="py-3">
                  <div className="font-mono text-[#00F0FF] font-semibold">{u.user_id || "—"}</div>
                  <div className="text-xs text-[#94A3B8]">{u.name} · {u.role}</div>
                </td>
                <td className="text-[#94A3B8]">{u.email}</td>
                <td className="text-right font-mono">{fmtUSD(u.funding_amount)}</td>
                <td className="text-right font-mono text-[#00F0FF]" data-testid={`user-rewards-${u.id}`}>{fmtUSD(u.rewards_generated)}</td>
                <td className="text-right font-mono">{u.active_plan_count ?? 0}</td>
                <td className="pl-4 font-mono text-xs text-[#94A3B8] truncate max-w-[160px]">{u.wallet_address || "—"}</td>
                <td className="text-right text-[#94A3B8]">{fmtDate(u.created_at)}</td>
                <td className="text-right"><StatusBadge status={u.status} /></td>
                <td className="text-right">
                  {u.role !== "admin" && (
                    <div className="inline-flex flex-wrap items-center justify-end gap-1.5 max-w-[260px]">
                      <input
                        type="number" placeholder="$"
                        value={addAmt[u.id] || ""}
                        onChange={(e) => setAddAmt({ ...addAmt, [u.id]: e.target.value })}
                        className="w-16 sm:w-20 bg-[#0A132A] border border-white/10 rounded px-2 py-1 text-xs"
                      />
                      <button
                        data-testid={`add-funds-${u.id}`}
                        onClick={() => {
                          const v = Number(addAmt[u.id] || 0);
                          if (v > 0) { act("add-funds", u.id, { amount: v }); setAddAmt({ ...addAmt, [u.id]: "" }); }
                        }}
                        className="px-2 py-1 bg-[#00F0FF] text-[#060B19] rounded text-xs font-bold hover:bg-[#00D4E0]"
                      >Credit</button>
                      {u.status === "active" ? (
                        <button onClick={() => act("suspend", u.id)} data-testid={`suspend-${u.id}`} className="px-2 py-1 bg-red-500/15 text-red-300 border border-red-500/30 rounded text-xs hover:bg-red-500/25">Suspend</button>
                      ) : (
                        <button onClick={() => act("activate", u.id)} data-testid={`activate-${u.id}`} className="px-2 py-1 bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 rounded text-xs hover:bg-emerald-500/25">Activate</button>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {pageRows.length === 0 && <tr><td colSpan={9} className="py-8 text-center text-[#475569]">No users found</td></tr>}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-4" data-testid="users-pagination">
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
            className="px-3 py-1.5 rounded-lg bg-[#0A132A] border border-white/10 text-sm disabled:opacity-40 hover:border-[#00F0FF]/40">Prev</button>
          <span className="text-sm text-[#94A3B8]">Page {page} of {totalPages}</span>
          <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}
            className="px-3 py-1.5 rounded-lg bg-[#0A132A] border border-white/10 text-sm disabled:opacity-40 hover:border-[#00F0FF]/40">Next</button>
        </div>
      )}
    </div>
  );
}
